import { useState } from "react";
import { Plus, Trash2, Layers, AlertTriangle } from "lucide-react";

const fmt = (n) =>
  n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CombiSimulator() {
  const [selections, setSelections] = useState(["1.80", "2.10"]);
  const [stake, setStake] = useState("10");

  const update = (i, v) =>
    setSelections((s) => s.map((x, idx) => (idx === i ? v : x)));
  const add = () => selections.length < 6 && setSelections((s) => [...s, "1.50"]);
  const remove = (i) =>
    selections.length > 2 && setSelections((s) => s.filter((_, idx) => idx !== i));

  const cotes = selections.map((s) => parseFloat(String(s).replace(",", ".")));
  const valid = cotes.every((c) => !isNaN(c) && c > 1);
  const mise = parseFloat(String(stake).replace(",", "."));
  const miseOk = !isNaN(mise) && mise > 0;

  const coteTotale = valid ? cotes.reduce((a, b) => a * b, 1) : null;
  const gain = valid && miseOk ? mise * coteTotale : null;
  const proba = valid ? 100 / coteTotale : null;

  return (
    <div
      data-testid="combi-simulator"
      className="my-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-[#0B1F3A]">
        <Layers className="h-5 w-5 text-[#2BB08A]" aria-hidden="true" />
        Simulateur de combiné
      </h3>
      <p className="mb-5 text-sm text-slate-500">
        Ajoutez de 2 à 6 sélections et observez comment les cotes se multiplient — et comment la
        probabilité diminue.
      </p>

      <div className="space-y-3">
        {selections.map((sel, i) => (
          <div key={i} className="flex items-center gap-3">
            <label
              htmlFor={`combi-odd-${i}`}
              className="w-24 shrink-0 text-sm font-medium text-slate-700"
            >
              Sélection {i + 1}
            </label>
            <input
              id={`combi-odd-${i}`}
              data-testid={`combi-input-odd-${i}`}
              type="number"
              min="1.01"
              step="0.01"
              inputMode="decimal"
              value={sel}
              onChange={(e) => update(i, e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-4 py-2 text-base transition-colors focus:border-[#2BB08A]"
              aria-label={`Cote de la sélection ${i + 1}`}
            />
            <button
              type="button"
              data-testid={`combi-remove-${i}`}
              onClick={() => remove(i)}
              disabled={selections.length <= 2}
              aria-label={`Supprimer la sélection ${i + 1}`}
              className="shrink-0 rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <button
          type="button"
          data-testid="combi-add"
          onClick={add}
          disabled={selections.length >= 6}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#2BB08A] px-4 py-2 text-sm font-semibold text-[#2BB08A] transition-colors hover:bg-[#E8F8F3] disabled:opacity-40"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Ajouter une sélection
        </button>
        <div className="flex items-center gap-2">
          <label htmlFor="combi-stake" className="text-sm font-medium text-slate-700">
            Mise (€)
          </label>
          <input
            id="combi-stake"
            data-testid="combi-input-stake"
            type="number"
            min="0"
            step="0.5"
            inputMode="decimal"
            value={stake}
            onChange={(e) => setStake(e.target.value)}
            className="w-28 rounded-lg border border-slate-300 px-4 py-2 text-base transition-colors focus:border-[#2BB08A]"
          />
        </div>
      </div>

      {valid && (
        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div data-testid="combi-result-odd" className="rounded-lg bg-[#F3F5F8] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Cote totale
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#0B1F3A]">
              {fmt(coteTotale)}
            </p>
          </div>
          <div data-testid="combi-result-gain" className="rounded-lg bg-[#F3F5F8] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Gain potentiel
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#0B1F3A]">
              {gain !== null ? `${fmt(gain)} €` : "—"}
            </p>
          </div>
          <div data-testid="combi-result-proba" className="rounded-lg bg-[#FEF3C7] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#78350F]">
              Probabilité implicite globale
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-[#78350F]">
              {proba.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %
            </p>
          </div>
        </div>
      )}
      {!valid && (
        <p data-testid="combi-error" className="mt-4 text-sm font-medium text-[#78350F]">
          Chaque cote doit être supérieure à 1,00.
        </p>
      )}

      <p
        data-testid="combi-warning"
        className="mt-5 flex items-start gap-2 rounded-lg border-l-4 border-[#E8A317] bg-[#FEF3C7] p-3 text-sm font-medium text-[#78350F]"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        Plus il y a de sélections, plus la probabilité que le pari soit gagnant diminue.
      </p>
    </div>
  );
}
